import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { sendSingleRequest } from '../../apis';
import { useWidth } from '../../lib/useWidth';
import { Button, CloseIcon, Datepicker, Input, Subtitle } from '../shared';
import FilesWall from '../shared/pictures-wall/FilesWall';
import { showFeedback } from '../../store/ui/ui.actions';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { IFileWall } from '../../entities/general.entities';
import { ILineResource } from '../../entities/farms.entities';
import ModalFeedbackView from '../shared/feedback/ModalFeedbackView';
import { toSecond } from '../../lib/common.helpers';

interface IForm {
  mussel_larvae: string | null;
  plankton: string | null;
  biofouling_larvae: string | null;
  comment: string | null;
  image_pdf_files: IFileWall[];
  report_date: number;
}

interface Props {
  lineData: ILineResource;
  onClose: () => void;
}

const defaultForm: IForm = {
  mussel_larvae: null,
  plankton: null,
  biofouling_larvae: null,
  comment: null,
  image_pdf_files: [],
  report_date: Date.now(),
};

const LineBioModal = ({ lineData, onClose }: Props) => {
  const width = useWidth();
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);

  const [disabled, setDisabled] = useState(false);
  const [formData, setFormData] = useState<IForm>({
    ...defaultForm,
  });

  const updateForm = (key: keyof IForm, value: any) => {
    setFormData(prv => ({ ...prv, [key]: value ? value : null }));
  };
  const handleConfirm = async () => {
    const form = {
      ...formData,
      report_date: toSecond(formData.report_date),
      line_id: lineData.id,
      rel_type: 'WATER_QUALITY',
    };

    setDisabled(true);
    const res = await sendSingleRequest(
      form,
      'POST',
      `api/report/biosecurity`,
      true,
    );
    if (res.status) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, 'Successfully submitted'),
        }),
      );
      onClose();
    } else {
      setDisabled(false);
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(lang, res.data?.message ?? 'Something went wrong'),
        }),
      );
    }
  };

  return (
    <Modal
      visible={true}
      onCancel={onClose}
      closable
      closeIcon={<CloseIcon />}
      width={700}
      footer={null}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-16'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, 'Water Quality')}
          </Subtitle>
        </div>
        <div className='pt-16'>
          <div className='pb-24'>
            <Input
              label={translate(lang, 'Mussel larvae')}
              placeholder=''
              type='text'
              value={formData.mussel_larvae ?? ''}
              onChange={e => updateForm('mussel_larvae', e.target.value)}
            />
          </div>
          <div className='pb-24'>
            <Input
              label={translate(lang, 'Plankton')}
              placeholder=''
              type='text'
              value={formData.plankton ?? ''}
              onChange={e => updateForm('plankton', e.target.value)}
            />
          </div>
          <div className='pb-24'>
            <Input
              label={translate(lang, 'Biofouling larvae')}
              placeholder=''
              type='text'
              value={formData.biofouling_larvae ?? ''}
              onChange={e => updateForm('biofouling_larvae', e.target.value)}
            />
          </div>
          <div className='pb-24'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder=''
              type='textarea'
              value={formData.comment ?? ''}
              onChange={e => updateForm('comment', e.target.value)}
            />
          </div>
          <div className='pb-24'>
            <Datepicker
              defaultValue={formData.report_date}
              label={translate(lang, 'Date')}
              onChange={e =>
                e && updateForm('report_date', e.toDate().getTime())
              }
            />
          </div>
          <div className='pb-24'>
            <FilesWall
              label={translate(lang, 'Images & PDF Files')}
              fileList={formData.image_pdf_files}
              onChange={v => updateForm('image_pdf_files', v)}
            />
          </div>
        </div>
      </div>
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          className='mr-32'
          width={width < 769 ? 'wide' : 'small'}
          size={2}
          type='fill'
          color='red'
          disabled={disabled}
          onClick={onClose}
        >
          {translate(lang, 'Close')}
        </Button>
        <Button
          width={width < 769 ? 'wide' : 'small'}
          size={2}
          type='fill'
          color='green'
          disabled={disabled}
          onClick={handleConfirm}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default LineBioModal;
